import {toBaseScalarPricingFormValues} from "@/features/pricing";
import {CallDurationOptions} from "../../../../calls/useCases/createService/service-form-call-duration-form-field/call-duration-form-field";
import {toPurchaseLimitFormValues} from "../../../../components/offering-form/general/settings/purchase-limit-payload";
import {GetLiveEventApiResponse} from "../../../../react-query-hooks/live-events/get-live-event/types";
import {EditLiveEventBasicFormStepFormValues} from "../schema-validation";

function toSavedClassSeries(liveEventData: GetLiveEventApiResponse) {
  if (liveEventData.topic_type !== "series") return [];

  return liveEventData.session_info.map((session) => {
    const isListedDuration = CallDurationOptions.find(
      (ele) => ele.value === session.duration.toString()
    );

    return {
      name: session.name,
      startDate: session.startDate,
      startTime: session.startTime,
      duration: isListedDuration ? session.duration.toString() : "-1",
      customDuration: isListedDuration ? undefined : session.duration,
    };
  });
}

function toSavedSingleSession(liveEventData: GetLiveEventApiResponse) {
  if (liveEventData.topic_type !== "single") {
    return {
      startDate: undefined,
      startTime: undefined,
      duration: undefined,
      customDuration: undefined,
    };
  }

  const [session] = liveEventData.session_info;
  const foundDurationOption = CallDurationOptions.find(
    (ele) => ele.value === session.duration.toString()
  );

  return {
    startDate: session.startDate,
    startTime: session.startTime,
    duration: foundDurationOption ? foundDurationOption.value : "-1",
    customDuration: foundDurationOption ? undefined : session.duration,
  };
}

/**
 * The saved live event in the edit page's form encoding.
 *
 * A tab that edits one part of the event, such as the checkout questions, still sends
 * the pricing, redirect and location with it, so all of those must go back exactly as
 * saved. The form tab uses this as its starting values and the checkout tab submits it
 * with its questions on top, so the two cannot drift.
 *
 * Cover images are left out: each tab loads them separately.
 */
export function toSavedEditLiveEventFormValues(
  liveEventData: GetLiveEventApiResponse,
  isPwywEnabled: boolean
): Omit<EditLiveEventBasicFormStepFormValues, "cover_images"> {
  const savedRedirectType =
    liveEventData.redirectType ?? (liveEventData.url_redirect ? "url" : "none");
  const savedPricing = toBaseScalarPricingFormValues(liveEventData, isPwywEnabled);
  const singleSession = toSavedSingleSession(liveEventData);

  return {
    attendees: liveEventData.attendees,
    attendeesType: liveEventData.attendees === -1 ? "unlimited" : "limited",
    callLocation:
      liveEventData.video_platform === "zoom"
        ? "zoom"
        : liveEventData.video_platform === "googleMeet"
        ? "google_meet"
        : liveEventData.video_platform === "physicalLocation"
        ? "physical_location"
        : liveEventData.is_custom_room
        ? "custom_url"
        : "video",
    locationAddress: liveEventData.location_address,
    locationLat: liveEventData.location_lat,
    locationLng: liveEventData.location_lng,
    isLocationPublic: liveEventData.is_location_public,
    callPrice: savedPricing.callPrice,
    callPriceCurrency: savedPricing.callPriceCurrency,
    callPriceType: savedPricing.callPriceType,
    classSeries: toSavedClassSeries(liveEventData),
    closeRegistration: liveEventData.statusName === "Paused",
    coverImage: liveEventData.cover_image,
    // Live events have always come back with no rows at all when the event is free.
    // Saved rows always carry an amount; the form's row type just does not say so.
    currencies:
      savedPricing.callPriceType === "free"
        ? []
        : savedPricing.currencies.map((row) => ({...row, callPrice: row.callPrice ?? 0})),
    enable_currency_conversion: true,
    selected_currencies: liveEventData.currencies
      .filter((x) => x.id)
      .filter((c) => c.is_auto_converted)
      .map((c) => c.currency.toUpperCase()),
    customDuration: singleSession.customDuration,
    customUrl: liveEventData.is_custom_room ? liveEventData.roomId : undefined,
    description: liveEventData.description,
    duration: singleSession.duration,
    hideFromProfile: liveEventData.hide_from_menu,
    isUrlRedirect: !!liveEventData.url_redirect,
    redirectAfterPurchase: savedRedirectType === "url" ? "custom-url" : savedRedirectType,
    telegramGroupId: liveEventData.telegramGroupChatId ?? undefined,
    name: liveEventData.name,
    requestCustomerPhoneNumber: liveEventData.request_phone_number,
    ...toPurchaseLimitFormValues(liveEventData.purchaseLimit),
    startDate: singleSession.startDate,
    startTime: singleSession.startTime,
    topicType: liveEventData.topic_type,
    url_redirect: liveEventData.url_redirect,
  };
}
